import * as React from 'react';
import {makeStyles} from '@material-ui/core';
import {Typography, Grid, TextField, Select, MenuItem} from '@material-ui/core';
import {Controller, useForm} from 'react-hook-form';
import * as yup from 'yup';
import {requiredFieldMessage} from 'utils/messages';
import {yupResolver} from '@hookform/resolvers/yup';
import Button from 'components/Button';
import {shiptmentService} from 'services/shipmentService';

type UpdateInputs = {
  shipmentId: string;
  trackingNumber: string;
  milestone:
    | `IT'S ON IT'S WAY`
    | `IN TRANSIT`
    | `TRANSIT DELAY`
    | `IN CUSTOMS`
    | `OUT FOR DELIVERY`
    | `DELIVERY ATTEMPT FAILED`
    | `READY FOR COLLECTION`
    | `DELIVERED`
    | `COLLECTED`
    | `PART DELIVERED`
    | `UNDELIVERABLE`;
};

const milestoneOptions = [
  {
    key: "IT'S ON IT'S WAY",
    value: "IT'S ON IT'S WAY",
    text: "It's On It's Way",
  },
  {key: 'IN TRANSIT', value: 'IN TRANSIT', text: 'In Transit'},
  {
    key: 'TRANSIT DELAY',
    value: 'TRANSIT DELAY',
    text: 'Transit Delay',
  },
  {key: 'IN CUSTOMS', value: 'IN CUSTOMS', text: 'In Customs'},
  {
    key: 'OUT FOR DELIVERY',
    value: 'OUT FOR DELIVERY',
    text: 'Out For Delivery',
  },
  {
    key: 'DELIVERY ATTEMPT FAILED',
    value: 'DELIVERY ATTEMPT FAILED',
    text: 'Delivery Attempt Failed',
  },
  {
    key: 'READY FOR COLLECTION',
    value: 'READY FOR COLLECTION',
    text: 'Ready For Collection',
  },
  {key: 'DELIVERED', value: 'DELIVERED', text: 'Delivered'},
  {key: 'COLLECTED', value: 'COLLECTED', text: 'Collected'},
  {
    key: 'PART DELIVERED',
    value: 'PART DELIVERED',
    text: 'Part Delivered',
  },
  {
    key: 'UNDELIVERABLE',
    value: 'UNDELIVERABLE',
    text: 'Undeliverable',
  },
];

const updateSchema = yup.object().shape({
  shipmentId: yup.string().trim().required(requiredFieldMessage('Shipment id')),
  trackingNumber: yup
    .string()
    .trim()
    .required(requiredFieldMessage('Tracking number')),
  milestone: yup.string().trim().required(requiredFieldMessage('Milestone')),
});

const ManageShipmentStatus: React.FC = () => {
  const classes = useStyles();

  const {control, handleSubmit, reset, getValues} = useForm<UpdateInputs>({
    mode: 'all',
    resolver: yupResolver(updateSchema),
    defaultValues: {
      shipmentId: '',
      trackingNumber: '',
      milestone: `IT'S ON IT'S WAY`,
    },
  });
  const onSubmit = async () => {
    try {
      await shiptmentService.sentShipmentStatus({
        shipmentId: getValues('shipmentId'),
        trackingNumber: getValues('trackingNumber'),
        milestone: getValues('milestone'),
      });
    } finally {
      reset({
        shipmentId: '',
        trackingNumber: '',
        milestone: `IT'S ON IT'S WAY`,
      });
    }
  };

  return (
    <div>
      <Typography component={'h1'} className={classes.title}>
        Shipment Status
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container className={classes.updateContainer} spacing={2}>
          <Grid item xs={9}>
            <Typography className={classes.updateText} component={'h2'}>
              Update
            </Typography>
          </Grid>
          <Grid
            container
            xs={3}
            spacing={2}
            alignItems="center"
            justify="flex-end"
          >
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                minWidth="0px"
                fullWidth
              >
                {'Submit'}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid container className={classes.updateOptionContainer} spacing={2}>
          <Grid item xs={3} className={classes.formElement}>
            <Typography className={classes.formLabel}>Shipment Id</Typography>
            <Controller
              name="shipmentId"
              control={control}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <>
                  <TextField
                    variant="outlined"
                    type="text"
                    placeholder="Enter Shipment Id"
                    fullWidth
                    margin="dense"
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                  />
                </>
              )}
            />
          </Grid>

          <Grid item xs={3} className={classes.formElement}>
            <Typography className={classes.formLabel}>
              Tracking Number
            </Typography>
            <Controller
              name="trackingNumber"
              control={control}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <>
                  <TextField
                    variant="outlined"
                    type="text"
                    placeholder="Enter Tracking Number"
                    fullWidth
                    margin="dense"
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                  />
                </>
              )}
            />
          </Grid>

          <Grid item xs={3} className={classes.formElement}>
            <Typography className={classes.formLabel}>Milestone</Typography>
            <Controller
              name="milestone"
              control={control}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <>
                  <Select
                    value={value}
                    onChange={onChange}
                    displayEmpty
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    className={classes.select}
                    error={!!error}
                  >
                    {milestoneOptions.map(milestone => (
                      <MenuItem value={milestone.value} key={milestone.key}>
                        {milestone.text}
                      </MenuItem>
                    ))}
                  </Select>
                  {error?.message && (
                    <Typography
                      component={'p'}
                      className={classes.errorMessage}
                    >
                      {error.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: 26,
    lineHeight: '40px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
      lineHeight: '28px',
    },
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(3),
  },
  errorMessage: {
    color: theme.palette.error.main,
    margin: '0 14px',
    fontSize: '0.75rem',
  },
  updateContainer: {
    width: '100%',
    background: '#fff',
    borderRadius: '5px 5px 0 0',
    padding: '12px 16px 15px',
    margin: 0,
    border: '1px solid',
    borderBottom: '0px',
    borderColor: theme.palette.grey[300],
  },
  updateOptionContainer: {
    width: '100%',
    background: '#fff',
    borderRadius: '0 0 5px 5px',
    padding: '12px 16px 15px',
    margin: 0,
    marginBottom: theme.spacing(3),
    border: '1px solid',
    borderColor: theme.palette.grey[300],
  },
  updateText: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '21px',
    lineHeight: '32px',
  },
  formElement: {},
  formLabel: {
    fontSize: 16,
    lineHeight: '28px',
  },
  select: {
    marginTop: '8px',
    marginBottom: '4px',
  },
  name: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
  },
}));

export default ManageShipmentStatus;
